import type { QueryClient } from '@tanstack/react-query'
import type { Locale } from '~/lib/i18n/shared'
import {
  createRootRouteWithContext,
  HeadContent,
  Outlet,
  Scripts,
} from '@tanstack/react-router'
import * as React from 'react'
import { Toaster } from 'sonner'
import { AuthGuardModal } from '~/components/auth/AuthGuardModal'
import { DefaultCatchBoundary } from '~/components/errors/default-catch-boundary'
import { NotFound404 } from '~/components/errors/not-found-404'
import { DefaultPending } from '~/components/misc/default-pending'
import { IntlProvider } from '~/components/providers/intl-provider'
import { LocalizedAuthProvider } from '~/components/providers/localized-auth-provider'

import { ThemeProvider } from '~/components/providers/theme-provider'
import { getCurrentLocale } from '~/lib/i18n/client'
import { defaultLocale } from '~/lib/i18n/shared'

import appCss from '~/styles/app.css?url'

interface RouterContext {
  queryClient: QueryClient
}

export const Route = createRootRouteWithContext<RouterContext>()({
  head: () => ({
    meta: [
      { charSet: 'utf-8' },
      { name: 'viewport', content: 'width=device-width, initial-scale=1' },
    ],
    links: [
      { rel: 'stylesheet', href: appCss },
      { rel: 'icon', href: 'data:image/svg+xml,%3Csvg xmlns=%22http://www.w3.org/2000/svg%22 viewBox=%220 0 32 32%22%3E%3Crect width=%2232%22 height=%2232%22 rx=%226%22 fill=%22%230f172a%22/%3E%3Cpath d=%22M8 22h16v3H8zm1-4 4-10 3 7 3-5 4 8z%22 fill=%22%23f8fafc%22/%3E%3C/svg%3E' },
    ],
  }),
  errorComponent: props => <DefaultCatchBoundary {...props} />,
  notFoundComponent: () => <NotFound404 />,
  pendingComponent: () => <DefaultPending />,
  component: RootComponent,
})

function RootComponent() {
  return (
    <RootDocument>
      <ThemeProvider>
        <IntlProvider>
          <LocalizedAuthProvider>
            <Outlet />
            <Toaster richColors position="top-right" />
            <AuthGuardModal />
          </LocalizedAuthProvider>
        </IntlProvider>
      </ThemeProvider>
    </RootDocument>
  )
}

/**
 * 正常页面的根文档，负责执行正常的国际化探嗅。
 */
function RootDocument({ children }: { children: React.ReactNode }) {
  let locale: Locale = defaultLocale
  try {
    locale = getCurrentLocale()
  }
  catch {
    // Ignore
  }

  return (
    <html lang={locale} suppressHydrationWarning>
      <head>
        <HeadContent />
      </head>
      <body>
        <React.Suspense fallback={<div>Loading...</div>}>
          {children}
        </React.Suspense>
        <Scripts />
      </body>
    </html>
  )
}
